import { create } from 'zustand';
import { TScheduleDay, TSearchAvailabilityStore } from './domain';
import { DEFAULT_SEARCH_AVAILABILITY_STATE } from './state';
import { format, startOfDay } from 'date-fns';
import { MONTH_FORMAT_DATE, SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { getAvailabilityByMonth } from '@/service/api/Availability/getAvailabilityByMonth';
import { isExpired10mins } from '@/service/utils/isExpired10mins';
import { getAvailabilityByDay } from '@/service/api/Availability/getAvailabilityByDay';
import { getPricesForSchedules } from '@/service/api/Prices/getPricesForSchedules';
import { mapSchedulesWithPrices } from '@/service/utils/mapSchedulesWithPrices';

export const useSearchAvailabilityStore = create<TSearchAvailabilityStore>((set, get) => ({
    ...DEFAULT_SEARCH_AVAILABILITY_STATE,
    initDate: async (partnerSlug, activities, date) => {
        set({
            activities,
            partnerSlug,
            isMonthLoading: true,
        });

        const newDate = date || get().selectedDate || new Date();

        if (!get().selectedDate && !!date) {
            set({
                selectedDate: newDateFromString(date),
            });
        }

        const currentDate = newDate ? newDateFromString(newDate) : startOfDay(new Date());
        const month = format(currentDate, MONTH_FORMAT_DATE);
        const activitiesList = activities.split(',');
        /* const daysInMonth = getAllLastDaysInMonth(currentDate); */
        let monthData = new Set<string>();

        /* Sync fetch months data
        for (const id of activitiesList) {
            const newMonthData = await getAvailabilityByMonth(partnerSlug, id, month);

            monthData = new Set([...monthData, ...newMonthData]);

            const isMonthFull = areArraysEqual(daysInMonth, [...monthData]);

            if (isMonthFull) {
                break;
            }
        } */

        const allMonths = await Promise.all(
            activitiesList.map((id) => getAvailabilityByMonth(partnerSlug, id, month))
        );

        monthData = new Set([...allMonths.flat()]);

        set((state) => ({
            months: {
                ...state.months,
                [currentDate.getMonth()]: {
                    timestamp: new Date().getTime(),
                    data: [...monthData],
                },
            },
            isMonthLoading: false,
        }));
    },
    selectDate: (newDate) => {
        const currentDate = newDate ? newDateFromString(newDate) : startOfDay(new Date());

        set({
            selectedDate: currentDate,
        });

        get().addSchedules(currentDate);
    },
    addMonth: (date) => {
        const { partnerSlug, activities, initDate, months } = get();

        const newDate = date ? newDateFromString(date) : new Date();
        const loadedMonth = months[newDate.getMonth()];

        if (!loadedMonth || isExpired10mins(loadedMonth.timestamp)) {
            set((state) => ({
                months: { ...state.months, [newDate.getMonth()]: undefined },
            }));
            initDate(partnerSlug, activities, newDate);
        }
    },
    addSchedules: async (date) => {
        const { partnerSlug, activities, schedules } = get();

        const newDate = date ? newDateFromString(date) : new Date();
        const dayKey = format(newDate, SERVER_FORMAT_DATE);
        const loadedDay = schedules[dayKey];

        if ((!loadedDay || isExpired10mins(loadedDay.timestamp)) && activities) {
            set((state) => ({
                schedules: { ...state.schedules, [dayKey]: undefined },
                isSchedulesLoading: true,
            }));

            try {
                const data = await Promise.all(
                    activities.split(',').map(async (activityId) => {
                        return await Promise.all([
                            getAvailabilityByDay(partnerSlug, activityId, dayKey),
                            getPricesForSchedules(partnerSlug, activityId, dayKey),
                        ]);
                    })
                );

                const newDayData = data.reduce((res, [newDayData, prices]) => {
                    const tripId = newDayData?.[0]?.trip_id;

                    if (tripId) {
                        res[newDayData[0].trip_id] = mapSchedulesWithPrices(newDayData, prices);
                    }

                    return res;
                }, {} as TScheduleDay);

                set((state) => ({
                    schedules: {
                        ...state.schedules,
                        [dayKey]: {
                            timestamp: new Date().getTime(),
                            data: newDayData,
                        },
                    },
                    isSchedulesLoading: false,
                }));
            } catch (err) {
                console.error('Error on fetching tripSchedules', err);
                set({
                    isSchedulesLoading: false,
                });
            }
        }
    },
    getSchedules: () => {
        const { selectedDate, schedules } = get();
        const newDate = selectedDate ? newDateFromString(selectedDate) : new Date();

        const dayKey = format(newDate, SERVER_FORMAT_DATE);
        const loadedDay = schedules[dayKey];

        return loadedDay?.data || undefined;
    },
}));
